import React, { useState, useMemo } from "react";
import styled from "styled-components";
import { Trans, useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { useStripe } from "@stripe/react-stripe-js";
import { postApi } from "../../../api/getUser";
import { getDeclineErrorMessage, formatNumber } from "../../../shared/helpers";
import {
  AddFormButton,
  FilterButton,
} from "../../../shared/components/TableElements";
import { LinkSpan, Desc } from "../../../shared/components/form/FormElements";
import {
  colorLightGray,
  colorTextAdditional,
  colorLightText,
} from "../../../utils/palette";
import CancelAddonModal from "./CancelAddonModal";
import ReactivateAddonModal from "./ReactivateAddonModal";
import ConfirmAddonModal from "./ConfirmAddonModal";
import { useAuth } from "../../../contexts/AuthContext";
import { EstimatedBox } from "./WexAddonRow";

const STATUS = {
  LOCKED: "locked",
  ELIGIBLE: "inactive",
  ACTIVE: "active",
  CANCELLING: "canceled",
};

const TelematicsAddonRow = ({
  status = STATUS.ELIGIBLE,
  isSubscriptionActive,
  isMobileSubscription,
  vehicleCount,
  unitPrice,
  currency,
  inputDuration,
  planName,
  totalVehicleSubAmount,
  periodEndDate,
  totalAfterCancellation,
  wexStatus,
  equipmentStatus,
  wexUnitPrice,
  equipmentUnitPrice,
  equipmentQuantity,
  onAddSuccess,
  onCancelSuccess,
  setShowMobileAddOnErrPopup,
  paymentData,
  currentProductId,
  onSubscribeClick,
}) => {
  const { t } = useTranslation("common");
  const stripe = useStripe();
  const { integrationsData } = useAuth();
  const history = useHistory();

  const isTelematicsProviderConnected = useMemo(
    () =>
      integrationsData?.some(
        (d) => d.provider_type === 1 && d.service_enabled === 1,
      ),
    [integrationsData],
  );

  const canToggle =
    status !== STATUS.LOCKED && (isSubscriptionActive || isMobileSubscription);
  const [isCollapsed, setIsCollapsed] = useState(status !== STATUS.LOCKED);

  const [loading, setLoading] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [showAddConfirm, setShowAddConfirm] = useState(false);
  const [error, setError] = useState("");

  const billingLabel = inputDuration === 2 ? t("year_sm") : t("month_sm");
  const grandTotalLabel =
    inputDuration === 2 ? t("new_yearly_total") : t("new_monthly_total");
  const billingNote = paymentData
    ? t("confirm_changes_billing_note")
    : undefined;

  const addonTotal = useMemo(
    () => Number(unitPrice) * Number(vehicleCount),
    [unitPrice, vehicleCount],
  );

  const estimatedTotal = useMemo(() => {
    if (totalVehicleSubAmount == null) return null;
    const wexTotal =
      wexStatus === STATUS.ACTIVE
        ? (wexUnitPrice || 0) * Number(vehicleCount)
        : 0;
    const equipTotal =
      equipmentStatus === STATUS.ACTIVE
        ? (equipmentUnitPrice || 0) * (equipmentQuantity || 0)
        : 0;
    return Number(totalVehicleSubAmount) + addonTotal + wexTotal + equipTotal;
  }, [
    addonTotal,
    totalVehicleSubAmount,
    wexStatus,
    wexUnitPrice,
    vehicleCount,
    equipmentStatus,
    equipmentUnitPrice,
    equipmentQuantity,
  ]);

  const subTitleText =
    status === STATUS.ACTIVE
      ? `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)} ${t("per")} ${billingLabel}`
      : status === STATUS.CANCELLING
        ? `${t("cancels_on")} ${periodEndDate || ""}`
        : `${currency} ${formatNumber(unitPrice, 2)} ${t("per")} ${t("vehicle_sm")} ${t("per")} ${billingLabel}`;

  const featureList = [
    t("telematics_feature_odometer"),
    t("telematics_feature_fault_codes"),
    t("telematics_feature_import_vehicles"),
  ];

  const handleAdd = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "add_telematics_addon" },
        "subscription",
      );
      if (res.success) {
        onAddSuccess();
      } else {
        if (res?.requires_action && res?.client_secret) {
          const confirmResult = await stripe.confirmCardPayment(
            res.client_secret,
          );
          if (confirmResult.paymentIntent) {
            onAddSuccess();
            return;
          } else if (confirmResult.error) {
            setError(
              getDeclineErrorMessage(
                "payment_intent_authentication_failure",
                t,
                "error_adding_telematics",
              ),
            );
            return;
          } else {
            setError(
              getDeclineErrorMessage(
                "payment_intent_authentication_failure",
                t,
                "error_adding_telematics",
              ),
            );
            return;
          }
        }
        setError(
          getDeclineErrorMessage(
            res.decline_code,
            t,
            "error_adding_telematics",
          ),
        );
      }
    } catch {
      setError(t("error_adding_telematics"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "cancel_telematics_addon" },
        "subscription",
      );
      if (res.success) {
        setShowCancelConfirm(false);
        onCancelSuccess();
      } else {
        setError(t("error_cancelling_telematics"));
      }
    } catch {
      setError(t("error_cancelling_telematics"));
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await postApi(
        { querystring: "add_telematics_addon" },
        "subscription",
      );
      if (res.success) {
        setShowReactivateModal(false);
        onAddSuccess();
      } else {
        if (res?.requires_action && res?.client_secret) {
          const confirmResult = await stripe.confirmCardPayment(
            res.client_secret,
          );
          if (confirmResult.paymentIntent) {
            setShowReactivateModal(false);
            onAddSuccess();
            return;
          } else if (confirmResult.error) {
            setError(
              getDeclineErrorMessage(
                "payment_intent_authentication_failure",
                t,
                "error_reactivating_telematics",
              ),
            );
            return;
          } else {
            setError(
              getDeclineErrorMessage(
                "payment_intent_authentication_failure",
                t,
                "error_reactivating_telematics",
              ),
            );
            return;
          }
        }
        setError(
          getDeclineErrorMessage(
            res.decline_code,
            t,
            "error_reactivating_telematics",
          ),
        );
      }
    } catch {
      setError(t("error_reactivating_telematics"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper $status={status} role="region" aria-label={t("telematics")}>
      <Header
        role={canToggle ? "button" : "heading"}
        aria-expanded={!isCollapsed}
        tabIndex={canToggle ? 0 : -1}
        onClick={() => canToggle && setIsCollapsed((prev) => !prev)}
        onKeyDown={(e) => {
          if (!canToggle) return;
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsCollapsed((prev) => !prev);
          }
        }}
      >
        <div className="d-flex gap-3 align-items-center">
          <TelematicsLogo>
            <i
              className="fi fi-rr-satellite"
              style={{
                fontSize: 20,
                color: "#4b5563",
                position: "relative",
                top: 3,
              }}
            />
          </TelematicsLogo>
          <div>
            <div className="d-flex align-items-center gap-2">
              <Title>{t("telematics")}</Title>
              {status === STATUS.ACTIVE ? (
                <ActiveBadge>{t("active")}</ActiveBadge>
              ) : status === STATUS.CANCELLING ? (
                <CancellingBadge>{t("cancelling_sm")}</CancellingBadge>
              ) : (
                <NewBadge>{t("new_sm")}</NewBadge>
              )}
            </div>
            <Subtitle $status={status}>{subTitleText}</Subtitle>
          </div>
        </div>
        <div className="d-flex align-items-center gap-3">
          {(status === STATUS.ACTIVE || status === STATUS.CANCELLING) && (
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {t("for_n_vehicles", { count: vehicleCount })}
              </div>
            </div>
          )}
          {canToggle && (
            <Chevron
              className={`fi fi-rr-angle-${isCollapsed ? "down" : "up"}`}
            />
          )}
        </div>
      </Header>

      {!isCollapsed && (
        <Body>
          {status === STATUS.CANCELLING ? (
            /* ── Cancelling state ── */
            <>
              <ReactivateAddonModal
                show={showReactivateModal}
                onHide={() => {
                  setError("");
                  setShowReactivateModal(false);
                }}
                title={t("reactivate_telematics_title")}
                addonName={t("telematics")}
                addonCost={`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
                onConfirm={handleReactivate}
                loading={loading}
                error={error}
              />

              <CancellingNotice>
                <i
                  className="fi fi-rr-triangle-warning"
                  style={{
                    position: "relative",
                    top: 2,
                    marginRight: 8,
                    flexShrink: 0,
                  }}
                />
                {t("addon_cancelling_notice", {
                  addonName: t("telematics"),
                  date: periodEndDate || "",
                })}
              </CancellingNotice>

              {isSubscriptionActive && (
                <div>
                  <LinkSpan onClick={() => setShowReactivateModal(true)}>
                    {t("reactivate_addon")}
                  </LinkSpan>
                </div>
              )}
            </>
          ) : status === STATUS.ACTIVE ? (
            /* ── Active state ── */
            <>
              <CancelAddonModal
                show={showCancelConfirm}
                onHide={() => {
                  setError("");
                  setShowCancelConfirm(false);
                }}
                title={t("cancel_telematics_title")}
                addonName={t("telematics")}
                periodEndDate={periodEndDate}
                inputDuration={inputDuration}
                addonCost={`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
                totalAfterCancellation={
                  totalAfterCancellation != null
                    ? `${currency} ${formatNumber(totalAfterCancellation, 2)}/${billingLabel}`
                    : null
                }
                onConfirm={handleCancel}
                loading={loading}
                error={error}
              />

              {!isTelematicsProviderConnected && (
                <SetupIncompleteNotice>
                  <i
                    className="fi fi-rr-link-alt"
                    style={{ position: "relative", top: 2, flexShrink: 0 }}
                  />
                  <span>
                    <Trans
                      i18nKey="no_telematics_provider_notice"
                      t={t}
                      components={[
                        <LinkSpan
                          style={{ fontSize: "14px" }}
                          onClick={() => history.push("/setup/integrations")}
                        />,
                      ]}
                    />
                  </span>
                </SetupIncompleteNotice>
              )}

              <PriceBox>
                <span style={{ fontSize: 13, color: "#6b7280" }}>
                  {`${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)}`}
                </span>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>
                    {`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
                  </div>
                  <Desc style={{ textAlign: "right" }}>
                    {t("for_n_vehicles", { count: vehicleCount })}
                  </Desc>
                </div>
              </PriceBox>

              <div>
                <LinkSpan
                  style={{ fontSize: 13, color: "#9ca3af" }}
                  onClick={() => setShowCancelConfirm(true)}
                >
                  {t("cancel_addon")}
                </LinkSpan>
              </div>
            </>
          ) : (
            /* ── Eligible / Locked state ── */
            <>
              <Section>
                <SectionTitle>
                  {t("telematics_capabilities_title")}
                </SectionTitle>
                <FeatureList>
                  {featureList.map((f) => (
                    <FeatureItem key={f}>
                      <i
                        className="fi fi-rs-check-circle"
                        style={{
                          color: "#16a34a",
                          position: "relative",
                          top: 2,
                        }}
                      />
                      <span>{f}</span>
                    </FeatureItem>
                  ))}
                </FeatureList>
                <ProviderNote>
                  {t("works_with_telematics_providers")}
                </ProviderNote>
              </Section>

              <PriceBox style={{ marginTop: "auto" }}>
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  {`${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)}`}
                </span>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>
                    {`${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`}
                  </div>
                  <Desc style={{ textAlign: "right" }}>
                    {t("for_n_vehicles", { count: vehicleCount })}
                  </Desc>
                </div>
              </PriceBox>

              {estimatedTotal !== null && (
                <EstimatedBox>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>
                      {t("estimated_total_label")}
                    </div>
                    <Desc>{t("vehicle_plan_plus_telematics")}</Desc>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>
                      {`${currency} ${formatNumber(estimatedTotal, 2)}/${billingLabel}`}
                    </div>
                    <Desc style={{ textAlign: "right" }}>
                      {t("with_plan_selected", {
                        planName: planName || t("plan"),
                      })}
                    </Desc>
                  </div>
                </EstimatedBox>
              )}

              <Footer>
                {status === STATUS.LOCKED && onSubscribeClick ? (
                  <FilterButton
                    type="button"
                    className="w-100"
                    onClick={onSubscribeClick}
                  >
                    {t("subscribe_to_add")}
                  </FilterButton>
                ) : (
                  <AddFormButton
                    type="button"
                    onClick={() => {
                      if (isMobileSubscription) {
                        setShowMobileAddOnErrPopup(true);
                        return;
                      }
                      setShowAddConfirm(true);
                    }}
                    disabled={status === STATUS.LOCKED}
                  >
                    {t("add_to_plan")}
                  </AddFormButton>
                )}
              </Footer>

              <ConfirmAddonModal
                show={showAddConfirm}
                onHide={() => {
                  setError("");
                  setShowAddConfirm(false);
                }}
                grandTotalLabel={grandTotalLabel}
                billingNote={billingNote}
                paymentData={paymentData}
                lineItems={[
                  ...(totalVehicleSubAmount != null
                    ? [
                        {
                          name: planName || "",
                          detail: vehicleCount
                            ? `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(totalVehicleSubAmount / vehicleCount, 2)}`
                            : undefined,
                          amount: `${currency} ${formatNumber(totalVehicleSubAmount, 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  {
                    name: t("telematics"),
                    detail: `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(unitPrice, 2)}`,
                    amount: `${currency} ${formatNumber(addonTotal, 2)}/${billingLabel}`,
                  },
                  ...(wexStatus === STATUS.ACTIVE
                    ? [
                        {
                          name: t("wex_fuel_card"),
                          detail: `${vehicleCount} ${t("vehicles_sm")} × ${currency} ${formatNumber(wexUnitPrice || 0, 2)}`,
                          amount: `${currency} ${formatNumber((wexUnitPrice || 0) * Number(vehicleCount), 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                  ...(equipmentStatus === STATUS.ACTIVE
                    ? [
                        {
                          name: t("small_equipment"),
                          detail: `${equipmentQuantity} ${t("equipment_sm")} × ${currency} ${formatNumber(equipmentUnitPrice || 0, 2)}`,
                          amount: `${currency} ${formatNumber((equipmentUnitPrice || 0) * (equipmentQuantity || 0), 2)}/${billingLabel}`,
                        },
                      ]
                    : []),
                ]}
                grandTotalAmount={(() => {
                  if (totalVehicleSubAmount == null) return undefined;
                  const wexTotal =
                    wexStatus === STATUS.ACTIVE
                      ? (wexUnitPrice || 0) * Number(vehicleCount)
                      : 0;
                  const equipTotal =
                    equipmentStatus === STATUS.ACTIVE
                      ? (equipmentUnitPrice || 0) * (equipmentQuantity || 0)
                      : 0;
                  return `${currency} ${formatNumber(totalVehicleSubAmount + addonTotal + wexTotal + equipTotal, 2)}/${billingLabel}`;
                })()}
                onConfirm={handleAdd}
                loading={loading}
                error={error}
                previewParams={{
                  new_product_id: Number(currentProductId),
                  duration: inputDuration,
                  quantity: vehicleCount,
                  equipment_quantity: equipmentQuantity || 0,
                  add_telematics: true,
                }}
                isAddonPurchase
              />

              {/* {status === STATUS.LOCKED && (
                <LockedHelperText>
                  <i
                    className="fi fi-rr-lock"
                    style={{ position: "relative", top: 2, marginRight: 6 }}
                  />
                  {t("subscribe_to_enable_addon")}
                </LockedHelperText>
              )} */}
            </>
          )}
        </Body>
      )}
    </Wrapper>
  );
};

export default TelematicsAddonRow;

// ── Styled components ──────────────────────────────────────────────────────

export const Wrapper = styled.div`
  border-radius: 5px;
  padding: 0.1rem 0.15rem;
  box-shadow: 2px 2px 2px lightgray;
  border: 1px solid
    ${(props) =>
      props.$status === STATUS.ACTIVE
        ? "#22c55e"
        : props.$status === STATUS.CANCELLING
          ? "#f97316"
          : "transparent"};
  background: #ffffff;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  &[role="button"] {
    cursor: pointer;
  }
`;

const Body = styled.div`
  padding: 0 12px 12px 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`;

const TelematicsLogo = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid ${colorLightGray};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const Title = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${(p) =>
    p.$status === STATUS.CANCELLING ? "red" : colorTextAdditional};
`;

const ActiveBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #dcfce7;
  color: #166534;
  text-transform: uppercase;
`;

const NewBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #dbeafe;
  color: #1d4ed8;
  text-transform: uppercase;
`;

const CancellingBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: #ffedd5;
  color: #c2410c;
  text-transform: uppercase;
`;

const CancellingNotice = styled.div`
  display: flex;
  align-items: flex-start;
  background: #fef9c3;
  border: 1px solid #fde68a;
  border-radius: 6px;
  padding: 12px 16px;
  font-size: 13.5px;
  color: #92400e;
  line-height: 1.5;
`;

const Chevron = styled.i`
  font-size: 18px;
  color: ${colorTextAdditional};
  margin-top: 4px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h5`
  font-weight: 500;
  margin-bottom: 0;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: ${colorLightText};
  font-size: 13.5px;
`;

const ProviderNote = styled.p`
  font-size: 13px;
  color: ${colorTextAdditional};
  font-style: italic;
  margin: 0;
`;

export const PriceBox = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f7f7f7;
  padding: 14px 14px;
  border-radius: 8px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const LockedHelperText = styled.p`
  font-size: 13px;
  color: ${colorTextAdditional};
  margin: 0;
  text-align: center;
`;

const SetupIncompleteNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 14px;
`;
